import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Plus, Edit, Trash2, Eye, Package, Printer, ShoppingCart, Search, X, Image, Video, Link as LinkIcon, Play, Settings } from 'lucide-react';
import { FileUploader } from './FileUploader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductDialog = ({ open, onOpenChange, product, categories, onSave }) => {
  const { t, i18n } = useTranslation();
  const defaultCategory = categories.length > 0 ? categories[0].name : '';
  const [formData, setFormData] = useState({
    name: '', description: '', name_en: '', name_ru: '', description_en: '', description_ru: '',
    price: '', original_price: '', category: defaultCategory, image_url: '', video_url: '',
    origin: '', brand: '', tags: [], stock: 100, is_active: true, badge: '',
    product_type: 'self', external_url: '',
    visible_languages: ['zh', 'en', 'ru'],  // 默认在所有语言可见
  });

  const getCategoryDisplayName = (cat) => {
    if (i18n.language === 'en' && cat.name_en) return `${cat.icon || ''} ${cat.name_en}`;
    if (i18n.language === 'ru' && cat.name_ru) return `${cat.icon || ''} ${cat.name_ru}`;
    return `${cat.icon || ''} ${cat.name}`;
  };

  useEffect(() => {
    if (product?.id) {
      setFormData({
        name: product.name || '', description: product.description || '',
        name_en: product.name_en || '', name_ru: product.name_ru || '',
        description_en: product.description_en || '', description_ru: product.description_ru || '',
        price: product.price?.toString() || '', original_price: product.original_price?.toString() || '',
        category: product.category || defaultCategory, image_url: product.image_url || '',
        video_url: product.video_url || '', origin: product.origin || '', brand: product.brand || '',
        tags: product.tags || [], stock: product.stock || 100, is_active: product.is_active !== false,
        badge: product.badge || '',
        product_type: product.product_type || 'self',
        external_url: product.external_url || '',
        visible_languages: product.visible_languages || ['zh', 'en', 'ru'],
      });
    } else {
      setFormData({
        name: '', description: '', name_en: '', name_ru: '', description_en: '', description_ru: '',
        price: '', original_price: '', category: '蔬菜', image_url: '', video_url: '',
        origin: '', brand: '', tags: [], stock: 100, is_active: true, badge: '',
        product_type: 'self', external_url: '',
        visible_languages: ['zh', 'en', 'ru'],
      });
    }
  }, [product]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      price: parseFloat(formData.price),
      original_price: formData.original_price ? parseFloat(formData.original_price) : null,
      stock: parseInt(formData.stock),
      video_url: formData.video_url || null,
      name_en: formData.name_en || null, name_ru: formData.name_ru || null,
      description_en: formData.description_en || null, description_ru: formData.description_ru || null,
      external_url: formData.product_type === 'external' ? formData.external_url : null,
      visible_languages: formData.visible_languages,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{product?.id ? t('admin.editProduct') : t('admin.addProduct')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* 产品类型选择 */}
          <Card className="bg-amber-50 border-amber-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-amber-700">📦 {t('admin.productType')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="radio" 
                    name="product_type" 
                    value="self" 
                    checked={formData.product_type === 'self'} 
                    onChange={(e) => setFormData({ ...formData, product_type: e.target.value })}
                    className="w-4 h-4 text-green-600"
                  />
                  <span className="font-medium">{t('admin.selfOperated')}</span>
                  <span className="text-sm text-gray-500">({t('admin.useCartOrder')})</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="radio" 
                    name="product_type" 
                    value="external" 
                    checked={formData.product_type === 'external'} 
                    onChange={(e) => setFormData({ ...formData, product_type: e.target.value })}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="font-medium">{t('admin.externalProduct')}</span>
                  <span className="text-sm text-gray-500">({t('admin.redirectExternal')})</span>
                </label>
              </div>
              
              {/* 外购产品显示外部链接输入 */}
              {formData.product_type === 'external' && (
                <div className="mt-4 space-y-2">
                  <Label className="text-amber-700">🔗 {t('admin.externalBuyLink')}</Label>
                  <Input 
                    value={formData.external_url} 
                    onChange={(e) => setFormData({ ...formData, external_url: e.target.value })} 
                    placeholder={t('admin.externalUrlPlaceholder')} 
                    data-testid="external-url-input"
                  />
                  <p className="text-xs text-gray-500">{t('admin.externalUrlHint')}</p>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>🇨🇳 {t('admin.productName')} ({t('admin.chinese')})</Label>
              <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required placeholder={t('admin.productNamePlaceholder')} data-testid="product-name-input" />
            </div>
            <div className="space-y-2">
              <Label>{t('products.category')}</Label>
              <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                <SelectTrigger data-testid="product-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (<SelectItem key={cat.id || cat.name} value={cat.name}>{getCategoryDisplayName(cat)}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <Card className="bg-blue-50 border-blue-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-blue-700">🌍 {t('admin.multiLang')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>🇬🇧 {t('admin.enName')}</Label>
                  <Input value={formData.name_en} onChange={(e) => setFormData({ ...formData, name_en: e.target.value })} placeholder="e.g., Organic Fresh Spinach" />
                </div>
                <div className="space-y-2">
                  <Label>🇷🇺 {t('admin.ruName')}</Label>
                  <Input value={formData.name_ru} onChange={(e) => setFormData({ ...formData, name_ru: e.target.value })} placeholder="например: Органический свежий шпинат" />
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-2">
            <Label>🇨🇳 {t('admin.productDesc')} ({t('admin.chinese')})</Label>
            <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required placeholder={t('admin.productDescPlaceholder')} data-testid="product-description-input" />
          </div>
          
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-4 space-y-3">
              <div className="space-y-2">
                <Label>🇬🇧 {t('admin.enDesc')}</Label>
                <Textarea value={formData.description_en} onChange={(e) => setFormData({ ...formData, description_en: e.target.value })} placeholder="Product description in English..." rows={2} />
              </div>
              <div className="space-y-2">
                <Label>🇷🇺 {t('admin.ruDesc')}</Label>
                <Textarea value={formData.description_ru} onChange={(e) => setFormData({ ...formData, description_ru: e.target.value })} placeholder="Описание товара на русском..." rows={2} />
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>{t('products.price')}</Label>
              <Input type="number" step="0.01" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} required data-testid="product-price-input" />
            </div>
            <div className="space-y-2">
              <Label>{t('admin.originalPrice')}</Label>
              <Input type="number" step="0.01" value={formData.original_price} onChange={(e) => setFormData({ ...formData, original_price: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>{t('admin.stock')}</Label>
              <Input type="number" value={formData.stock} onChange={(e) => setFormData({ ...formData, stock: e.target.value })} required data-testid="product-stock-input" />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>{t('admin.productImage')}</Label>
            <div className="flex gap-2">
              <Input value={formData.image_url} onChange={(e) => setFormData({ ...formData, image_url: e.target.value })} placeholder={t('admin.imageUrlPlaceholder')} className="flex-1" data-testid="product-image-url-input" />
              <FileUploader type="image" accept="image/jpeg,image/png,image/gif,image/webp" onUpload={(url) => setFormData({ ...formData, image_url: url })} label={t('common.upload')} />
            </div>
            {formData.image_url && (<img src={formData.image_url} alt={t('admin.preview')} className="w-24 h-24 object-cover rounded mt-2" />)}
          </div>
          
          <div className="space-y-2">
            <Label>{t('admin.productVideo')}</Label>
            <div className="flex gap-2">
              <Input value={formData.video_url || ''} onChange={(e) => setFormData({ ...formData, video_url: e.target.value })} placeholder={t('admin.videoUrlPlaceholder')} className="flex-1" />
              <FileUploader type="video" accept="video/mp4,video/webm,video/quicktime" onUpload={(url) => setFormData({ ...formData, video_url: url })} label={t('common.upload')} />
            </div>
            {formData.video_url && (<video src={formData.video_url} controls className="w-full max-h-48 rounded mt-2" />)}
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('admin.origin')}</Label>
              <Input value={formData.origin} onChange={(e) => setFormData({ ...formData, origin: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>{t('admin.brand')}</Label>
              <Input value={formData.brand} onChange={(e) => setFormData({ ...formData, brand: e.target.value })} />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('admin.tags')}</Label>
              <Input value={formData.tags.join(', ')} onChange={(e) => setFormData({ ...formData, tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) })} placeholder={t('admin.tagsPlaceholder')} />
            </div>
            <div className="space-y-2">
              <Label>{t('admin.badge')}</Label>
              <Select value={formData.badge || 'none'} onValueChange={(v) => setFormData({ ...formData, badge: v === 'none' ? '' : v })}>
                <SelectTrigger><SelectValue placeholder={t('admin.badgeNone')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('admin.badgeNone')}</SelectItem>
                  <SelectItem value="sale">{t('admin.badgeSale')}</SelectItem>
                  <SelectItem value="新品">{t('admin.badgeNew')}</SelectItem>
                  <SelectItem value="热销">{t('admin.badgeHot')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <input type="checkbox" id="is_active" checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} className="rounded" data-testid="product-active-checkbox" />
            <Label htmlFor="is_active">{t('admin.active')}</Label>
          </div>
          
          {/* 语言可见性设置 */}
          <Card className="bg-purple-50 border-purple-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-purple-700">🌐 {t('admin.languageVisibility', '语言版本可见性')}</CardTitle>
              <CardDescription className="text-xs">{t('admin.languageVisibilityDesc', '选择产品在哪些语言版本显示')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4">
                {[
                  { code: 'zh', name: '中文', flag: '🇨🇳' },
                  { code: 'en', name: 'English', flag: '🇬🇧' },
                  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
                ].map(lang => (
                  <label key={lang.code} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.visible_languages?.includes(lang.code)}
                      onChange={(e) => {
                        const newLangs = e.target.checked
                          ? [...(formData.visible_languages || []), lang.code]
                          : (formData.visible_languages || []).filter(l => l !== lang.code);
                        setFormData({ ...formData, visible_languages: newLangs });
                      }}
                      className="rounded"
                    />
                    <span>{lang.flag} {lang.name}</span>
                  </label>
                ))}
              </div>
            </CardContent>
          </Card>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
            <Button type="submit" className="bg-green-600 hover:bg-green-700" data-testid="save-product-button">{t('common.save')}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Admin Products Component
const AdminProducts = ({ products, onRefresh, productCategories, onCategoryRefresh }) => {
  const [editProduct, setEditProduct] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editCategory, setEditCategory] = useState(null);
  const [newCategory, setNewCategory] = useState({ name: '', name_en: '', name_ru: '', icon: '' });
  const { token } = useAuth();
  const { t, i18n } = useTranslation();

  const getCategoryName = (cat) => {
    if (i18n.language === 'en' && cat.name_en) return cat.name_en;
    if (i18n.language === 'ru' && cat.name_ru) return cat.name_ru;
    return cat.name;
  };

  const handleSaveCategory = async () => {
    if (!newCategory.name) {
      toast.error(t('admin.categoryNameRequired', '请输入分类名称'));
      return;
    }
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (editCategory) {
        await axios.put(`${API}/categories/${editCategory.id}`, newCategory, { headers });
        toast.success(t('admin.categoryUpdated', '分类已更新'));
      } else {
        await axios.post(`${API}/categories`, { ...newCategory, type: 'product' }, { headers });
        toast.success(t('admin.categoryCreated', '分类已创建'));
      }
      setNewCategory({ name: '', name_en: '', name_ru: '', icon: '' });
      setEditCategory(null);
      onCategoryRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm(t('admin.confirmDeleteCategory', '确定删除此分类吗？'))) return;
    try {
      await axios.delete(`${API}/categories/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t('admin.categoryDeleted', '分类已删除'));
      onCategoryRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    }
  };

  const handleSave = async (productData) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (editProduct?.id) {
        await axios.put(`${API}/products/${editProduct.id}`, productData, { headers });
        toast.success(t('admin.updateSuccess'));
      } else {
        await axios.post(`${API}/products`, productData, { headers });
        toast.success(t('admin.createSuccess'));
      }
      setIsDialogOpen(false);
      setEditProduct(null);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('admin.operationFailed'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('admin.confirmDelete'))) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/products/${id}`, { headers });
      toast.success(t('admin.deleteSuccess'));
      onRefresh();
    } catch (err) {
      toast.error(t('admin.deleteFailed'));
    }
  };

  return (
    <div data-testid="admin-products">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">{t('admin.products')}</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setCategoryDialogOpen(true)} data-testid="manage-categories-button">
            <Settings className="w-4 h-4 mr-2" /> {t('admin.manageCategories', '管理分类')}
          </Button>
          <Button className="bg-green-600 hover:bg-green-700" onClick={() => { setEditProduct({}); setIsDialogOpen(true); }} data-testid="add-product-button">
            <Plus className="w-4 h-4 mr-2" /> {t('admin.addProduct')}
          </Button>
        </div>
      </div>

      {/* Category Management Dialog */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('admin.manageCategories', '管理分类')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Add new category */}
            <Card className="bg-green-50 border-green-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">{editCategory ? t('admin.editCategory', '编辑分类') : t('admin.addCategory', '添加分类')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-4 gap-2">
                  <Input placeholder="🥬" value={newCategory.icon} onChange={(e) => setNewCategory({...newCategory, icon: e.target.value})} className="w-16" />
                  <Input placeholder="分类名称" value={newCategory.name} onChange={(e) => setNewCategory({...newCategory, name: e.target.value})} />
                  <Input placeholder="English Name" value={newCategory.name_en} onChange={(e) => setNewCategory({...newCategory, name_en: e.target.value})} />
                  <Input placeholder="Русское название" value={newCategory.name_ru} onChange={(e) => setNewCategory({...newCategory, name_ru: e.target.value})} />
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleSaveCategory} size="sm">
                    {editCategory ? t('common.save') : t('admin.add', '添加')}
                  </Button>
                  {editCategory && (
                    <Button variant="outline" size="sm" onClick={() => { setEditCategory(null); setNewCategory({ name: '', name_en: '', name_ru: '', icon: '' }); }}>
                      {t('common.cancel')}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Category list */}
            <div className="space-y-2">
              {productCategories.map((cat) => (
                <div key={cat.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{cat.icon}</span>
                    <div>
                      <div className="font-medium">{cat.name}</div>
                      <div className="text-xs text-gray-500">{cat.name_en} / {cat.name_ru}</div>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => { setEditCategory(cat); setNewCategory({ name: cat.name, name_en: cat.name_en || '', name_ru: cat.name_ru || '', icon: cat.icon || '' }); }}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteCategory(cat.id)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('admin.image')}</TableHead>
                <TableHead>{t('admin.name')}</TableHead>
                <TableHead>{t('admin.type')}</TableHead>
                <TableHead>{t('admin.category')}</TableHead>
                <TableHead>{t('admin.price')}</TableHead>
                <TableHead>{t('admin.stock')}</TableHead>
                <TableHead>{t('admin.status')}</TableHead>
                <TableHead>{t('admin.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {products.map((product) => (
                <TableRow key={product.id}>
                  <TableCell><img src={product.image_url} alt={product.name} className="w-12 h-12 object-cover rounded" /></TableCell>
                  <TableCell className="font-medium">{product.name}</TableCell>
                  <TableCell>
                    <Badge variant={product.product_type === 'external' ? 'outline' : 'default'} className={product.product_type === 'external' ? 'border-blue-500 text-blue-600' : 'bg-green-600'}>
                      {product.product_type === 'external' ? t('admin.externalShort') : t('admin.selfShort')}
                    </Badge>
                  </TableCell>
                  <TableCell>{product.category}</TableCell>
                  <TableCell>¥{product.price.toFixed(2)}</TableCell>
                  <TableCell>{product.product_type === 'external' ? '-' : product.stock}</TableCell>
                  <TableCell><Badge variant={product.is_active ? 'default' : 'secondary'}>{product.is_active ? t('admin.active') : t('admin.inactive')}</Badge></TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button size="icon" variant="ghost" onClick={() => { setEditProduct(product); setIsDialogOpen(true); }} data-testid={`edit-product-${product.id}`}><Edit className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" className="text-red-500" onClick={() => handleDelete(product.id)} data-testid={`delete-product-${product.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <ProductDialog open={isDialogOpen} onOpenChange={setIsDialogOpen} product={editProduct} categories={productCategories} onSave={handleSave} />
    </div>
  );
};


export default AdminProducts;
